import { useCurrentPermissionsStore } from '@onereach/permissions-ui';
import { createRouter, createWebHistory, type RouteRecordRaw } from 'vue-router';

import config from '@/config';
import { useAuthStore } from '@/stores/auth/auth';
import { checkIfIframe } from '@/utils/checkIfIframe';

import helpers from '../helpers/index';

import routes from './routes';
import { IframeStatusMessages } from './utils/iframe-routing/constants';
import {
  processIframeEvents,
  sendIframeResponseEvent,
  validateIframeActions,
  waitForIframeEvent,
} from './utils/iframe-routing/methods';


const flatRoutes = routes.flatMap((route): RouteRecordRaw[] => {
  if (!route.children?.length) {
    return [route];
  }
  return [route, ...route.children.map(child => ({
    ...child,
    meta: {
      auth: route.meta?.auth,
    },
  }))];
});

const router = createRouter({
  history: createWebHistory(),
  routes,
});

let eventTriggered = false;

router.beforeEach(async (to) => {
  const route = flatRoutes.find(r => r.name === to.name);

  if (!route) {
    return;
  }
  const authStore = useAuthStore();

  // iframe logic
  if (checkIfIframe() && to.meta?.iframeEventRequired) {
    if (!eventTriggered) {
      const eventData = await waitForIframeEvent();
      eventTriggered = true;

      if (validateIframeActions(eventData.action)) {
        return processIframeEvents(eventData.action);
      } else {
        sendIframeResponseEvent(IframeStatusMessages.InvalidAction);
      }
    }
    eventTriggered = false;
  }


  let redirectPathQuery = to.query.redirectPath || '';
  redirectPathQuery = helpers.removeURLParameter(redirectPathQuery as string, 'token');
  if (redirectPathQuery) {
    authStore.setRedirectUrl(redirectPathQuery);
  }
  if (to.meta?.authMult) {
    await authStore.validateMultiUser().catch(() => {});
    sessionStorage.setItem('redirect-mult', to.fullPath);

    // Crutch for single-user tests
    if (to.name !== 'profile-activity') {
      if (!authStore.multiUser) return '/logout';
    }
  }

  if (!to.meta?.allowMfaRequired && authStore.mfaRequired) {
    return '/profile/security';
  }

  if (['logout', 'validate-payment'].includes(route.name as string)
    || /multi-user/.test((typeof route.name == 'string' && route.name)
    || '',
    )) {
    return true;
  }

  if (!to.meta?.allowSuspended && authStore.isSuspended) {
    return '/account/payments';
  }

  if (!to.meta?.allowForceChangePassword && authStore.forceChangePassword) {
    return '/profile/security';
  }

  if (authStore.authorized == null && to.name !== 'auth-provider') {
    const initialPath = to.path;
    try {
      await authStore.init({
        initialPath,
        clearCookies: !to.meta?.authMult,
      });

      if (!to.meta?.allowForceChangePassword && authStore.forceChangePassword) {
        return '/profile/security';
      }

      if (!to.meta?.allowMfaRequired && authStore.mfaRequired) {
        return '/profile/security';
      }

      if (initialPath === '/account/payments') {
        try {
          await authStore.getAccountIsPayable();
          return authStore.isPayable ? true : '/account';
        } catch {
          return '/account';
        }
      }

      // The embedded flow-permissions editor has no use for payments state; skip the extra requests there
      if (to.name !== 'iframe-flow-permissions') {
        authStore.getAccountIsPayable();
      }

      if (!to.meta?.allowSuspended && authStore.isSuspended) {
        return '/account/payments';
      }
    } catch {
      // authMult routes (e.g. /profile/security) may be entered with only a multi-user
      // session; missing user cookies should not force logout in that case.
      if (to.meta?.authMult && authStore.multiUser) {
        // fall through to permission checks / allow navigation
      } else if (route.path === '*' || route.meta?.auth) {
        return '/logout';
      } else {
        return true;
      }
    }
  }

  // setRedirectUrl parses the return path as an absolute url
  if (to.meta?.authMult && authStore.multiUser && authStore.authorized === false
    && to.matched.some(matchedRoute => matchedRoute.name === 'account')) {
    return {
      path: '/multi-user/list-users',
      query: { redirectPath: new URL(to.fullPath, window.location.origin).href },
    };
  }

  if (to.meta?.permission && config.PERMISSIONS_API_URL) {
    const permissionsStore = useCurrentPermissionsStore();
    if (!permissionsStore.currentUserPermissions) {
      try {
        await permissionsStore.getCurrentPermissions();
      } catch {
        // a throw here aborts the navigation, on the first load that is a blank page
        return '/account';
      }
    }
    if (!permissionsStore.hasPermission(to.meta.permission as string)) {
      return '/account';
    }
  }

  return true;
});

router.afterEach((to) => {
  if (to.meta?.title) {
    document.title = to.meta.title as string;
  }
});

export default router;
